/**
 * Central HTTP client.
 *
 * - Reads base URL from REACT_APP_API_URL (env)
 * - Injects the access token from tokenStore on every request
 * - Auto-refreshes the access token on 401 (one retry)
 * - Normalises responses to the envelope { success, data } / { success:false, error }
 *
 * No external dependency on axios — kept on fetch so we don't grow the bundle.
 */

import { tokenStore } from './tokenStore';

const API_BASE = (process.env.REACT_APP_API_URL || '/api/v1').replace(/\/$/, '');

class ApiError extends Error {
  constructor(message, status, code, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

let refreshPromise = null;

async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  const refreshToken = tokenStore.getRefreshToken();
  if (!refreshToken) {
    return Promise.reject(new ApiError('No refresh token', 401, 'NO_REFRESH'));
  }
  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success) {
        tokenStore.clear();
        const err = body.error || {};
        throw new ApiError(err.message || 'Refresh failed', res.status, err.code, err.details);
      }
      tokenStore.set(body.data.accessToken, body.data.refreshToken, body.data.user);
      return body.data.accessToken;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

async function rawRequest(path, options, attemptedRefresh = false) {
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  const token = tokenStore.getAccessToken();

  const headers = {
    Accept: 'application/json',
    ...(options.body != null && !(options.body instanceof FormData)
      ? { 'Content-Type': 'application/json' }
      : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const res = await fetch(url, {
    ...options,
    headers,
    body:
      options.body != null && !(options.body instanceof FormData) && typeof options.body !== 'string'
        ? JSON.stringify(options.body)
        : options.body,
  });

  // 204 No Content
  if (res.status === 204) return null;

  let body;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (res.status === 401 && !attemptedRefresh && tokenStore.getRefreshToken() && !path.startsWith('/auth/')) {
    try {
      await refreshAccessToken();
      return rawRequest(path, options, true);
    } catch {
      tokenStore.clear();
      window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason: 'refresh-failed' } }));
      throw new ApiError('Session expired', 401, 'SESSION_EXPIRED');
    }
  }

  if (!res.ok || (body && body.success === false)) {
    const err = (body && body.error) || {};
    throw new ApiError(
      err.message || `HTTP ${res.status}`,
      res.status,
      err.code || `HTTP_${res.status}`,
      err.details,
    );
  }

  return body && Object.prototype.hasOwnProperty.call(body, 'data') ? body.data : body;
}

export const api = {
  get: (path, options = {}) => rawRequest(path, { ...options, method: 'GET' }),
  post: (path, body, options = {}) => rawRequest(path, { ...options, method: 'POST', body }),
  put: (path, body, options = {}) => rawRequest(path, { ...options, method: 'PUT', body }),
  patch: (path, body, options = {}) => rawRequest(path, { ...options, method: 'PATCH', body }),
  delete: (path, options = {}) => rawRequest(path, { ...options, method: 'DELETE' }),
};

export { ApiError, API_BASE };
